import {fetchServer, YummyServerError} from "./helpers/functions.js";
import type {
    AnimeRatingsJson,
    IAnimeFavoriteJson,
    IBanJson,
    IBloggerVideoAnimeJson,
    IClaimedMessageJson,
    ICommentable,
    ICommentJson,
    ICommentJsonComplaint,
    IEditJson,
    IFriendJson,
    IFriendStatus,
    IMessageEditionWriteJson,
    IMessageHistoryJson,
    IOneAnimeJson,
    IOneAnimeSmallJson,
    IOneAppJson,
    IOneMessageDialogJson,
    IOneMessageJson,
    IOneVideoJson,
    IProfileJson,
    IReviewJson,
    ITrailerJson,
    IUserJson,
    RegisterFormData,
    VideoBodyJson
} from "../../../nodeserver/structs/structs";
import {DataStore} from "./helpers/datastore.js";
import type {FilterAnimeGetParams, PostMessageVote, UsersSearchGetParams} from "../../../nodeserver/structs/getparams";


export const APIGateway = "/api"

abstract class IApiMethods {
    method = this.api.method.bind(this.api);

    constructor(protected readonly api: Api) {
    }
}

class AnimeList extends IApiMethods {
    public setIt(animeId: number, list: number, date?: number) {
        const g = date ? {date: date} : {};
        return this.api.method<{ list: string }>(`/anime/${animeId}/list`, 'PUT', {list: list, ...g})
    }

    public setFavorite(animeId: number, date?: number) {
        return this.api.method(`/anime/${animeId}/list/fav`, 'PUT', date ? {date: date} : {});
    }

    public removeFavorite(animeId: number) {
        return this.api.method(`/anime/${animeId}/list/fav`, 'DELETE');
    }

    public removeIt(animeId: number) {
        return this.api.method(`/anime/${animeId}/list`, 'DELETE');
    }

    public getUsersLists(animeId: number) {
        return this.api.method<{ list_id: number; count: number }[]>(`/anime/${animeId}/lists`, 'GET')
            .then(e => e.response);
    }
}

class AnimeRate extends IApiMethods {
    public setRate(animeId: number, rate: number) {
        return this.api.method<{ rating: number; votes: number }>(`/anime/${animeId}/rate`, 'PUT', {
            rate: rate
        });
    }

    public removeRate(animeId: number) {
        return this.api.method<{ rating: number; votes: number }>(`/anime/${animeId}/rate`, 'DELETE');
    }

    public getAnimeRates(animeId: number) {
        return this.api.method<AnimeRatingsJson[]>(`/anime/${animeId}/rates`, 'GET')
            .then(e => e.response);
    }
}

class Comments extends IApiMethods {
    public getList(objectType: ICommentable, objectId: number, skip: number = 0, sort: 'new' | 'old' | 'nice' = 'new') {
        return this.api.method<{ comments: ICommentJson[], isMod: boolean }>(
            `/comments/${objectType}/${objectId}`, 'GET', {skip, sort}
        ).then(e => e.response);
    }

    public getClaimedComments(offset: number = 0, limit: number = 20) {
        return this.api.method<{ comments: ICommentJsonComplaint[], isMod: boolean }>('/comments/claimed', 'GET', {offset, limit})
            .then(e => e.response);

    }

    public getChildren(parentCommentId: number, skip: number = 0) {
        return this.api.method<{ comments: ICommentJson[], isMod: boolean }>(
            `/comments/${parentCommentId}/children`, 'GET', {skip}
        ).then(e => e.response)
    }

    /**
     *
     * @param commentId - Comment ID
     * @param isLike - if false - add dislike
     */
    public addLike(commentId: number, isLike: boolean) {
        return this.api.method<{ likes: number; dislikes: number }>(
            `/comments/${commentId}/vote`,
            'PUT',
            {action: isLike ? 1 : -1}
        ).then(e => e.response);
    }

    public removeLike(commentId: number) {
        return this.api.method<{ likes: number; dislikes: number }>(
            `/comments/${commentId}/vote`,
            'DELETE'
        ).then(e => e.response);
    }

    public addComment(objectType: ICommentable, objectId: number, params: {
        reply_to_comment?: number;
        parent_comment?: number;
        text: string;
        // 'g-recaptcha-token': string;
    }) {
        return this.method<ICommentJson>(`/comments/${objectType}/${objectId}`, 'POST', params)
            .then(e => e.response);
    }

    public removeClaim(commentId: number) {
        return this.method(`/comments/${commentId}/claim`, 'DELETE');
    }

    public edit(commentId: number, data: { text?: string; spoiler?: boolean }) {
        return this.method<{ text: string }>(`/comments/${commentId}`, 'PATCH', data);
    }

    /**
     * Remove your own comment
     * @param commentId
     */
    public remove(commentId: number): Promise<true>;
    /**
     * Remove comment by admin
     * @param commentId
     * @param params
     */
    public remove(commentId: number, params: {
        reason: number;
        forever: number;
        remove_children: number
    }): Promise<true>;
    public remove(commentId: number, params?: any): Promise<true> {
        return this.method(`/comments/${commentId}`, 'DELETE', params)
            .then(e => e.response);
    }

    async claim(commentId: number, why: number) {
        return this.method(`/comments/${commentId}/claim`, 'PUT', {why})
            .then(e => e.response);
    }

    /**
     * This method can be called only by admin
     * @param number
     */
    async restore(number: number) {
        await this.api.method(`/comments/${number}/restore`, 'POST');
    }
}

class Reviews extends IApiMethods {
    public addLike(reviewId: number, isLike: boolean) {
        return this.api.method<{ likes: number; dislikes: number }>(
            `/reviews/${reviewId}/vote`,
            'PUT',
            {action: isLike ? 1 : -1}
        ).then(e => e.response);
    }

    public removeLike(reviewId: number) {
        return this.api.method<{ likes: number; dislikes: number }>(
            `/reviews/${reviewId}/vote`,
            'DELETE'
        ).then(e => e.response);
    }

    public getByAnime(page_id: number, params: { limit?: number, offset?: number, sort?: 'new' | 'old' | 'top' }) {

        return this.method<{ reviews: IReviewJson[], can_add: boolean }>(`/anime/${page_id}/reviews`, 'GET', params)
            .then(e => e.response);
    }

    async update(reviewId: number, params: {
        rating?: {
            average?: number; category: {
                category: string,
                average: number
            }[]
        },
        text?: string,
        anime_id?: number,
        commentable?: boolean,
        published?: 0 | 1 | -1,
        decline_comment?: string,
    }) {
        return this.method<{ review_id: number }>(`/reviews/${reviewId}`, 'PATCH', params)
            .then(e => e.response);
    }

    async create(param: {
        rating: { average: number; category: { average: number; category: string }[] };
        text: string;
        anime_id: number;
        commentable: boolean
    }) {
        return this.method<{ review_id: number }>(`/reviews`, 'POST', param)
            .then(e => e.response);
    }
}

class Notifications extends IApiMethods {
    public removeAll() {
        return this.api.method('/profile/notifications', 'DELETE')
            .then(e => e.response)
    }

    public removeById(notificationId: number) {
        return this.api.method(`/profile/notifications/${notificationId}`, 'DELETE')
            .then(e => e.response);
    }
}

class Profile extends IApiMethods {
    public readonly notifications = new Notifications(this.api)

    public getLoginVkState() {
        return this.api
            .method<{ state: string }>(`/profile/login/vk/state`, "GET", {
                to: location.pathname + location.search,
            })
            .then((e) => e.response.state);
    }

    public async updateOnline() {
        const hash = await this.getHash();
        return this.api.method('/profile/online', 'POST', {hash}).then(e => e.response);
    }

    public async register(values: RegisterFormData) {
        values.hash = await this.getHash();
        return this.api.method<{ user_id: number }>('/users', 'POST', values)
            .then(e => e.response);
    }

    public async getToken(): Promise<string> {
        return this.method<string>("/profile/token", 'GET').then(e => e.response)
    }

    public setSettings(valuesDict: { [p: string]: any }): Promise<any> {
        return this.api.method('/profile', 'PATCH', valuesDict);
    }

    public login(login: string, password: string): Promise<boolean>;

    public login(login: string, password: string, captchaKey?: string): Promise<boolean>;

    public login(login: string, password: string, captchaKey: string | null = null): Promise<boolean> {
        return this.api.method('/profile/login', 'POST', {
            login: login,
            password: password,
            ...(captchaKey ? {recaptcha_response: captchaKey} : {})
        }).then(e => e.response)

    }

    public randomAva() {
        const randomTypes = `waifu,neko,shinobu,megumin,bully,cuddle,cry,hug,awoo,kiss,lick,pat,smug,bonk,yeet,blush,smile,wave,highfive,handhold,nom,bite,glomp,slap,kill,kick,happy,wink,poke,dance,cringe`
            .split(',');
        return fetch(`https://api.waifu.pics/sfw/${randomTypes[Math.floor(Math.random() * randomTypes.length)]}`)
            .then(e => e.json())
            .then(e => e['url'] as string);

    }

    public logout() {
        return this.method<boolean>("/profile/logout", "POST").then(e => e.response);
    }

    public async removeAvatar(userId: number) {
        return this.method<boolean>(`/users/${userId}/avatar`, "DELETE").then(e => e.response);
    }

    public async setAvatar(userId: number, avatarData: Blob) {
        return this.method(`/users/${userId}/avatar`, 'PUT', avatarData).then(e => e.response);
    }

    async get(): Promise<IProfileJson> {
        return this.method<IProfileJson>('/profile', 'GET')
            .then(e => e.response);
    }

    private async getHash(): Promise<string> {
        try {
            return ((window as any)['yaCounter92092678'] as any).getClientID()
        } catch (e) {
        }
        try {
            await new Promise(r => setTimeout(r, 200));
            const clientId = await new Promise((r, fail) => {
                try {
                    ym(92092678, 'getClientID', clientId => r(clientId));
                } catch (e) {
                    fail();
                }
                setTimeout(fail, 300);
            });
            return clientId! as string;
        } catch (e) {
        }
        return $.cookie('_ym_uid') || '';

    }
}

class Friends extends IApiMethods {
    public getFriends(userId: number, offset: number = 0, limit: number = 20): Promise<IFriendJson[]> {
        return this.method<IFriendJson[]>(`/users/${userId}/friends`, 'GET', {offset, limit})
            .then(e => e.response);
    }

    public getFriendsList(userId: number, list: IFriendStatus, offset: number = 0, limit: number = 20): Promise<IFriendJson[]> {
        return this.method<IFriendJson[]>(`/users/${userId}/friends/${list}`, 'GET', {offset, limit})
            .then(e => e.response);
    }

    public delete(friendId: number) {
        return this.method<true>(`/users/${user_id}/friends/${friendId}`, 'DELETE')
            .then(e => e.response);
    }

    public add(friendId: number) {
        return this.method<true>(`/users/${user_id}/friends/${friendId}`, 'PUT')
            .then(e => e.response)
    }

    async isFriend(friendId: number): Promise<IFriendStatus | null> {
        return this.method<{ status: IFriendStatus | null }>(`/users/${user_id}/friends/${friendId}`, 'GET')
            .then(e => e.response.status)
    }
}

class Users extends IApiMethods {
    friends = new Friends(this.api);

    public filter(filters: UsersSearchGetParams) {
        return this.method<{ items: IUserJson[], limit: number, offset: number }>('/users', 'GET', filters)
            .then(e => e.response);
    }

    /**
     * get all user's anime lists
     * @param userId
     */
    public getLists(userId: number): Promise<IAnimeFavoriteJson[]>;

    /**
     * get user's anime lists with specific list id
     * @param userId
     * @param listId - filter by list id
     */
    public getLists(userId: number, listId: number): Promise<IAnimeFavoriteJson[]>;

    public getLists(userId: number, listId?: number) {
        return this.method<IAnimeFavoriteJson[]>(`/users/${userId}/lists${typeof listId == "number" ? '/' + listId : ''}`, 'GET')
            .then(e => e.response);
    }

    public removeLists(userId: number, listId?: number) {
        return this.method<true>(`/users/${userId}/lists${typeof listId == "number" ? '/' + listId : ''}`, 'DELETE')
            .then(e => e.response);

    }

    public getUserInfo(userId: number): Promise<IUserJson>;

    public getUserInfo(userNickName: string): Promise<IUserJson>;

    public getUserInfo(userIdOrNickName: number | string): Promise<IUserJson> {
        return this.method<IUserJson>(`/users/${typeof userIdOrNickName == "number" ? 'id' + userIdOrNickName : userIdOrNickName}`, 'GET').then(e => e.response)
    }

    public checkUserExists(userId: number): Promise<boolean>;

    public checkUserExists(nickName: string): Promise<boolean>;

    public async checkUserExists(userIdOrNickName: string | number): Promise<boolean> {
        try {
            await this.method<boolean>(`/users/${typeof userIdOrNickName == "number" ? 'id' + userIdOrNickName : userIdOrNickName}`, 'HEAD');
            return true;
        } catch (e) {
            if (e instanceof YummyServerError && e.code === 404)
                return false;
            throw e;
        }
    }

    public banUser(userId: number, data: { reason?: string; until: number }) {
        return this.method(`/users/${userId}/ban`, 'PUT', data)
            .then(e => e.response)
    }

    public unbanUser(userId: number) {
        return this.method(`/users/${userId}/ban`, 'DELETE')
            .then(e => e.response)
    }

    public getBannedUsers(params: { start_from?: number } = {}): Promise<IBanJson[]> {
        return this.method<IBanJson[]>('/users/banned', 'GET', params)
            .then(e => e.response);
    }


    async update(userId: number, data: Partial<IUserJson>) {
        return this.method(`/users/${userId}`, 'PATCH', data)
            .then(e => e.response);
    }
}

class Messages extends IApiMethods {
    public restoreClaim(claimId: number) {
        return this.api.method<true>(`/dialogs/messages/claims/${claimId}/restore`, "POST").then(t => t.response);
    }

    async argueClaim(claimId: number) {
        return this.api.method<true>(`/dialogs/messages/claims/${claimId}/argue`, "POST").then(t => t.response);
    }

    rejectClaim(claimId: number) {
        return this.api.method<true>(`/dialogs/messages/claims/${claimId}/reject`, "POST").then(t => t.response);
    }

    approveClaim(claimId: number) {
        return this.api.method<true>(`/dialogs/messages/claims/${claimId}/approve`, "POST").then(t => t.response);
    }

    public getClaimed(page: number, messageId?: number, limit: number = 20) {
        return this.api.method<IClaimedMessageJson[]>(`/dialogs/messages/claims`, 'GET', {
            message_id: messageId ?? 0,
            page: page,
            limit
        }).then(t => t.response)
    }

    public getDialogs(offset: number): Promise<IOneMessageDialogJson[]>;
    public getDialogs(offset: number, getAlso: number): Promise<IOneMessageDialogJson[]>;
    public getDialogs(offset: number, getAlso: number = 0): Promise<IOneMessageDialogJson[]> {
        return this.api.method<{ dialogs: IOneMessageDialogJson[] }>(
            `/dialogs`, 'GET', {
                offset: offset,
                ...(getAlso ? {need_uid: getAlso} : {})
            }).then(e => e.response.dialogs);
    }

    public getMessages(peerId: number, startFrom: number = 0) {
        return this.api.method<IOneMessageJson[]>(
            `/dialogs/${peerId}/messages`, 'GET', startFrom !== 0 ? {
                start_from: startFrom
            } : {}).then(e => e.response);
    }

    public banUser(peerId: number, isBan: boolean, userId?: number, unbanDelta?: number, reason?: string) {
        const data: { [k: string]: any } = {}
        if (typeof userId === 'number') data['user_id'] = userId;
        if (typeof unbanDelta === 'number') data['unbandelta'] = unbanDelta;
        if (typeof reason === 'string') data['reason'] = reason;
        return this.api.method(`/dialogs/${peerId}/ban`, isBan ? 'POST' : 'DELETE', data);
    }

    public sendMessage(toId: number, message: { message: string, answer_msg_id: number | null }) {
        return this.method<true>(`/dialogs/${toId}/messages`, 'POST', message)
            .then(e => e.response);
    }

    public claimMessage(messageId: number, vote: PostMessageVote) {
        return this.method<true>(`/dialogs/messages/${messageId}/claim`, 'POST', vote)
            .then(r => r.response)
    }

    public editMessage(messageId: number, newText: String) {
        return this.method<IMessageEditionWriteJson>(`/dialogs/messages/${messageId}`, 'PUT', {
            new_text: newText
        }).then(e => e.response);
    }

    public deleteMessage(messageId: number) {
        return this.method<IMessageEditionWriteJson>(`/dialogs/messages/${messageId}`, 'DELETE')
            .then(e => e.response);
    }

    public restoreMessage(messageId: number) {
        return this.method<IMessageEditionWriteJson>(`/dialogs/messages/${messageId}/restore`, 'POST', {
            // messageId: messageId,
            restore: true
        }).then(e => e.response);
    }

    public messageHistory(messageId: number) {
        return this.method<IMessageHistoryJson[]>(`/dialogs/messages/${messageId}/history`, 'GET')
            .then(e => e.response);
    }

    public readMessages(userId: number) {
        return this.method<{ ok: true }>(`/dialogs/${userId}/messages/read`, 'POST')
            .then(e => e.response);
    }
}

class Video extends IApiMethods {
    private token?: string;
    private _tokenGetCoroutine?: Promise<string>;

    public watchVideo(videoId: number, currentTime: number, times: number[]): Promise<boolean> {
        // if(!user_id) return Promise.resolve(true);
        return this.method(`/video/${videoId}`, 'PUT', {time: currentTime, times}, {keepAlive: true})
            .then(e => e.response)

        // if (user_id && !this.token) {
        //     this._tokenGetCoroutine ??= this.api.profile.getToken();
        //     this.token = await this._tokenGetCoroutine;
        // }
        // const headers: { [k: string]: any } = {
        //     'Content-Type': 'application/json',
        // };
        // if (this.token) {
        //     headers['Authorization'] = `Bearer ${this.token}`;
        // }
        // const ans = await fetch(`https://watch.yani.tv/video/${videoId}`, {
        //     method: 'PUT',
        //     body: JSON.stringify({time: currentTime}),
        //     headers
        // }).then(e => e.json()) as { response: true };
        // return ans.response;
    }

    // public async removeWatch(videoId: number) {
    //     if (user_id && !this.token) {
    //         this._tokenGetCoroutine ??= this.api.profile.getToken();
    //         this.token = await this._tokenGetCoroutine;
    //     }
    //     const headers: { [k: string]: any } = this.token ? {Authorization: `Bearer ${this.token}`} : {};
    //     const ans = await fetch(`https://watch.yani.tv/video/${videoId}`, {
    //         method: 'DELETE',
    //         headers
    //     }).then(e => e.json()) as { response: true };
    //     return ans.response;
    // }

    public async updateAllWatches() {
        const watches = await DataStore.getAllVideos();
        return this.method('/video', 'POST', {videos: watches});
    }

    async subscribeTo(id: number) {
        return this.method(`/video/${id}/subscribe`, 'PUT').then(e => e.response);
    }

    async unsubscribeFrom(id: number) {
        return this.method(`/video/${id}/subscribe`, 'DELETE').then(e => e.response);
    }
}

class BloggerVideo extends IApiMethods {
    public add(data: VideoBodyJson) {
        return this.method<{ video_id: number }>(`/bloggers/video`, 'POST', data)
            .then(e => e.response.video_id)
    }

    public edit(videoId: number, data: VideoBodyJson) {
        return this.method<true>(`/bloggers/video/${videoId}`, 'PUT', data)
            .then(e => e.response);
    }

    public delete(videoId: number) {
        return this.method<true>(`/bloggers/video/${videoId}`, 'DELETE')
            .then(e => e.response);
    }
}

class Bloggers extends IApiMethods {
    public readonly video = new BloggerVideo(this.api);

    public subscribe(bloggerId: number): Promise<true> {
        return this.method<true>(`/bloggers/${bloggerId}/subscribe`, 'PUT', {subscribe: true})
            .then(e => e.response);
    }

    public unsubscribe(bloggerId: number) {
        return this.method<true>(`/bloggers/${bloggerId}/subscribe`, 'DELETE')
            .then(e => e.response);
    }

    public getAnimeVideos(animeId: number, offset: number = 0) {
        return this.method<IBloggerVideoAnimeJson[]>(`/anime/${animeId}/bloggervideos`, 'GET', {offset})
            .then(e => e.response);
    }

}

class Trailers extends IApiMethods {
    public get(animeId: number): Promise<ITrailerJson[]> {
        return this.method<ITrailerJson[]>(`/anime/${animeId}/trailers`, 'GET')
            .then(e => e.response);
    }

}

class Posts extends IApiMethods {
    delete(postId: number): Promise<true> {
        return this.method(`/posts/${postId}`, 'DELETE')
            .then(e => e.response)
    }
}

class Utils extends IApiMethods {
    getData(period: string) {
        return this.method(`/utils/stat/${period}`, 'GET').then(e => e.response);
    }
}

class Apps extends IApiMethods {
    public create(name: string, description?: string) {
        return this.api.method<{ app_id: number }>(
            '/apps',
            'POST',
            {name: name, description: description}
        )
            .then(e => e.response.app_id);
    }

    public getApps(data: { offset: number; limit: number }) {
        return this.api.method<IOneAppJson[]>(
            '/apps',
            'GET',
            data
        )
            .then(e => e.response);
    }

    public deleteApp(appId: number) {
        return this.api.method(`/apps/${appId}`, 'DELETE')
            .then(e => e.response);
    }

    public getAppById(appId: number) {
        return this.api.method<IOneAppJson>(`/apps/${appId}`, 'GET')
            .then(e => e.response);
    }

    public update(appId: number, {name, description}: { name?: string, description?: string }) {
        return this.api.method<true>(`/apps/${appId}`, 'PATCH', {name, description})
            .then(e => e.response);
    }

    public getStatistics(appId: number, period: string): Promise<({
        data: { [k: number]: number },
        start: number,
        end: number
    })> {
        return this.method<{
            data: { [k: number]: number },
            start: number,
            end: number
        }>(`/apps/${appId}/stat?period=${period}`, 'GET')
            .then(e => e.response);
    }
}

class Api {
    public readonly anime = {
        list: new AnimeList(this),
        rate: new AnimeRate(this),
        get(id: string | number) {
            return this.list.method<IOneAnimeJson>(`/anime/${id}`, 'GET')
                .then(e => e.response);
        },
        search(query: string, limit: number = 5, offset: number = 0) {
            return this.list.method<IOneAnimeSmallJson[]>(
                '/search',
                'GET',
                {q: query, limit, offset}
            )
                .then(e => e.response);
        },
        getRecommendations(animeId: number) {
            return this.list.method<IOneAnimeJson[]>(`/anime/${animeId}/recommendations`, 'GET')
                .then(e => e.response);
        },
        doNotRecommend(animeId: number) {
            return this.list.method<true>(`/anime/${animeId}/recommend`, 'DELETE')
                .then(e => e.response);
        },
        recommend(animeId: number) {
            return this.list.method<true>(`/anime/${animeId}/recommend`, 'PUT')
                .then(e => e.response);
        },
        filter(filters: FilterAnimeGetParams): Promise<IOneAnimeJson[]> {
            return this.list.method<IOneAnimeJson[]>('/anime', 'GET', filters)
                .then(e => e.response);
        },
        trailers: new Trailers(this),
        getVideos(page_id: number) {
            return this.list.method<IOneVideoJson[]>(`/anime/${page_id}/videos`, 'GET').then(e => {
                for (const i of e.response) {
                    i.data.player = "Плеер " + i.data.player.replace("Плеер ", "")
                }

                return e.response;
            });
        }
    };
    public readonly users = new Users(this);
    public readonly comments = new Comments(this);
    public readonly profile = new Profile(this);
    public readonly reviews = new Reviews(this);
    public readonly messages = new Messages(this);
    public readonly video = new Video(this);
    public readonly bloggers = new Bloggers(this);
    public readonly posts = new Posts(this);
    public readonly utils = new Utils(this);
    public readonly apps = new Apps(this);

    constructor(private readonly format: 'json' | 'xml' | 'json5') {
    }

    method<T = true>(path: string, method: 'POST' | 'GET' | 'DELETE' | 'HEAD' | 'OPTIONS' | 'PATCH' | 'PUT', data?: {
        [k: string]: any
    } | Blob, options?: { keepAlive: boolean }) {
        const params: RequestInit = {headers: {}};
        const exists = Object.keys(data ?? {}).length > 0;
        if (exists || data instanceof Blob) {
            if ((method !== 'GET' && method !== 'HEAD') || data instanceof Blob) {
                if (data instanceof Blob) {
                    params.body = data;
                    //@ts-ignore
                    params.headers['Content-type'] = 'data/raw';
                } else {
                    params.body = JSON.stringify(data!);
                    //@ts-ignore
                    params.headers['Content-type'] = 'application/json'
                }
            } else {
                path += '?' + new URLSearchParams(data);
            }
        }
        if (options?.keepAlive) params.keepalive = true;
        //@ts-ignore
        params.headers['Vary'] = this.format;
        //@ts-ignore
        params.headers['X-Application'] = 'wawegr8j13it4rdw';
        //@ts-ignore
        params.headers["Lang"] = __language;
        return fetchServer<{ response: T }>(APIGateway + path, method, params);
    }

    public getEdits(param: { offset: number; limit: number }) {
        return this.method<IEditJson[]>('/edits', 'GET', param)
            .then(e => e.response);
    }
}

const instance = new Api('json');

//@ts-ignore
window['ServerApi'] = instance;
export {instance as ServerApi}